import React, { useEffect, useState } from "react";
import { Star, Loader2, CheckCircle2, Send } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";

/** صف تقييم صغير يظهر تحت نتيجة كل منتج: ٥ نجوم + تعليق عند عدم الرضا (≤3) — يصل للأدمن فورًا. */
export const ResultRating = ({ product, contentId = "", projectId = "", className = "" }) => {
  const { t, lang } = useLang();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setRating(0); setDone(false); setComment("");
    if (!contentId) return;
    api.get("/product-ratings/mine", { params: { product, content_id: contentId } }).then((r) => { if (r.data?.rating) { setRating(r.data.rating); setDone(true); } }).catch(() => {});
  }, [product, contentId]);

  const send = async (r = rating, c = comment) => {
    setSaving(true);
    try {
      await api.post("/product-ratings", { product, content_id: contentId, project_id: projectId, rating: r, comment: c, lang });
      setDone(true);
      toast.success(r >= 4 ? t("شكرًا! سعداء أن النتيجة أعجبتك", "Thanks! Glad you liked the result") : t("وصلنا رأيك — سنحسّن النتيجة ونتواصل معك", "Got it — we'll improve this and follow up"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const pick = (n) => { setRating(n); if (n >= 4) send(n, ""); else setDone(false); };

  return (
    <div className={`mt-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-xs ${className}`} data-testid={`result-rating-${product}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-muted-foreground">{done ? t("تقييمك لهذه النتيجة:", "Your rating:") : t("قيّم هذه النتيجة:", "Rate this result:")}</span>
        <div className="flex gap-0.5" dir="ltr">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" disabled={saving} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => pick(n)}
              className="transition-transform hover:scale-110" data-testid={`result-star-${product}-${n}`} aria-label={`${n}`}>
              <Star className={`w-4 h-4 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
            </button>
          ))}
        </div>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
        {done && !saving && <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> {t("شكرًا لك", "Thank you")}</span>}
      </div>
      {rating > 0 && rating <= 3 && !done && (
        <div className="mt-2 flex gap-2 items-start" data-testid={`result-rating-comment-${product}`}>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={600}
            placeholder={t("ما الذي لم يعجبك؟ (الألوان، الشكل، النص…) — سنعمل على تحسينه", "What didn't you like? (colours, shape, text…) — we'll improve it")}
            className="flex-1 bg-background border border-border rounded-lg p-2 text-xs resize-none" data-testid={`result-rating-text-${product}`} />
          <Button size="sm" onClick={() => send()} disabled={saving} className="h-8 gap-1 bg-primary text-white" data-testid={`result-rating-send-${product}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {t("إرسال", "Send")}
          </Button>
        </div>
      )}
    </div>
  );
};
